import Commons from "oc-commons/Commons";
import Schrank from "./Schrank";
import Preisberechnung from "./partsAndPrices/Preisberechnung";
import Helper from '../model/Helper';
import Raum from "./Raum";
import {v2, v3} from "oc-threejs/short3";
import Line2d from "oc-threejs/math/Line2d";
import ClosedInterval from "oc-commons/ClosedInterval";
import ArrangementO3 from "../object3d/Arrangment";
import Kunde from "./Kunde";
import Maths3 from "oc-threejs/Maths3";

export default class Arrangement {

    // persistent (UserLayer) ------------------------------------------

    type: string;
    schraenke: Schrank[] = [];
    bemerkungen = null;
    kunde: Kunde=new Kunde();

    selected = {
        schrankIdx: 0,
        elementIdx: -1,
        interieur: null,
    };

    griffArtikelnummer: string = Arrangement.defaultGriff;

    // transient (CompiledLayer) und cached -----------------------------

    object3d: ?THREE.Object3D;

    // transient (CompiledLayer) und resetted vor Compilierung ----------

    raum: Raum;

    // Map I18N-Key auf Parameter-Array, meist leer
    hinweistexteMap = {};

    preisberechnung: Preisberechnung;
    arrangementPreis: number;
    partsList = {
        schraenke:[],
        model:{}
    };
    doorsClosed = true;

    // eine Map Dekortyp -> Material.Name
    materials = {};

    schiebetuerLinksVorne = false;
    schliessdaempfung = false;
    fluegelanordungLinks = false;

    constructor() {
        this.preisberechnung = new Preisberechnung(this);
        this.raum = new Raum(this);
        this.materials = APP.createDekor2DefaultMaterial();
    }

    static get defaultGriff() {
        return APP.settings.getValue('default_griff', 'Bogengriff');
    }

    get selectedSchrank() {
        return this.schraenke[this.currentSchrankIndex];
    }

    set selectedSchrank(schrank) {
        this.currentSchrankIndex = this.schraenke.findIndex(s => s === schrank);
    }

    get currentSchrankIndex() {
        return this.selected.schrankIdx || 0;
    }

    set currentSchrankIndex(idx) {
        this.selected.schrankIdx = idx;
        this.selected.elementIdx = -1;
    }

    get selectedElement() {
        if (this.selected.elementIdx==-1) {
            return null;
        }
        return this.selectedSchrank?.elements[this.selected.elementIdx];
    }

    set selectedElement(element) {
        if (element) {
            this.selectedSchrank = element.schrank;
            this.selected.elementIdx = this.selectedSchrank.elements.findIndex(e => e === element);
            this.selected.interieur = null;
        } else {
            this.selected.elementIdx = -1;
        }
    }

    get selectedInterieur() {
        return this.selected.interieur;
    }

    set selectedInterieur(interieur) {
        if (interieur!=null && interieur.isOrphaned()) {
            interieur = null;
        }
        this.selected.interieur = interieur;
        this.selected.elementIdx = -1;
    }

    ensureElementSelected() {
        if (this.selectedElement==null) {
            this.selectedElement = _.first(this.selectedSchrank.elements);
            APP.model.setDirty();
        }
    }

    get hasInterieurDoors() {
        return this.schraenke.some(s=>s.hasInterieurDoors);
    }

    get hasFronts() {
        return this.schraenke.some(s=>s.hasFronts);
    }

    toJSON() {
        let result = {
            Modell: _.clone(this.Modell),
            changeableModell: false,
            currentSchrank: this.currentSchrankIndex,
            schraenke: this.schraenke.map(c => c.toJSON()),
            partsList: this.partsList,
            preis: this.preisberechnung.getPreis(),
            bemerkungen: this.bemerkungen,
            kunde:this.kunde.toJson(),
            materials: _.clone(this.materials),
            griffArtikelnummer: this.griffArtikelnummer,
            schiebetuerLinksVorne: this.schiebetuerLinksVorne,
            schliessdaempfung: this.schliessdaempfung,
            fluegelanordungLinks: this.fluegelanordungLinks
        };
        result.schrank = {
            gesamtpreis: result.preis
        };
        return result;
    }

    fromJSON(json) {
        this.Modell = _.clone(json.Modell);
        this.changeableModell = json.changeableModell || false;
        this.schraenke = Commons.mapreuse(json.schraenke||[],this.schraenke,c => new Schrank(this),(d,s)=>d.fromJSON(s));
        this.currentSchrankIndex = json.currentSchrank || 0;
        this.bemerkungen = json.bemerkungen;
        _.assign(this.materials,json.materials);
        if (json.kunde) {
            this.kunde.fromJson(json.kunde);
        }
        this.griffArtikelnummer = Commons.isEmpty(json.griffArtikelnummer) ? Arrangement.defaultGriff : json.griffArtikelnummer;
        this.schiebetuerLinksVorne = json.schiebetuerLinksVorne || false;
        this.schliessdaempfung = json.schliessdaempfung || false;
        this.fluegelanordungLinks = json.fluegelanordungLinks || false;
        this.heal();
        return this;
    }

    heal() {
        APP.healMaterials(this.materials,false);

        const anzahlVorher = this.schraenke.length;
        const anzahl = this.isEckschrank() ? 2 : 1;

        Commons.setArrayLength(this.schraenke,anzahl,()=>{
            const schrank = new Schrank(this);
            schrank.fromJSON({});
            schrank.size.x=240;
            schrank.size.y=250;
            schrank.size.z=60;
            schrank.sizeTop.x=110;
            schrank.sizeTop.y=120;
            schrank.sizeTop.z=30;
            return schrank;
        });

        if (this.isEckschrank()) {
            if (anzahlVorher!=anzahl) {
                let [hoehe1, breite1, tiefe1, breite2, tiefe2] = APP.settings.getValue('eckschrank_default_masse').split(',').map(s => parseFloat(s));
                this.schraenke[0].x = breite1;
                this.schraenke[0].y = hoehe1;
                this.schraenke[0].z = tiefe1;
                this.schraenke[1].x = breite2;
                this.schraenke[1].y = hoehe1;
                this.schraenke[1].z = tiefe2;
                this.convertEckschrankGesamtMasseToSchenkelMasse();
            }
        }

        if (this.griffArtikelnummer==null) {
            this.griffArtikelnummer = Arrangement.defaultGriff;
        }
        if (this.isShojituer() && !['Shojigriff','OhneGriff','Griffmulde'].includes(this.griffArtikelnummer)) {
            this.griffArtikelnummer = 'Griffmulde';
        }
    }

    setStandardSize() {
        if (!this.isEckschrank()) {
            this.schraenke.forEach(schrank=>{
                schrank.size.x = this.Modell.BreiteStandard;
                schrank.size.y = this.Modell.HoeheStandard;
                schrank.size.z = this.Modell.TiefeStandard;
            });
        }
    }

    convertEckschrankGesamtMasseToSchenkelMasse() {
        const [a,b] = this.schraenke.map(s=>s.size);
        if (a.z < b.z) {
            a.x -= b.z;
        } else {
            b.x -= a.z;
        }
    }

    copy(arrangement,createInterceptor) {
        this.bemerkungen = arrangement.bemerkungen;
        this.schraenke = Commons.mapreuse(arrangement.schraenke,this.schraenke||[],()=>new Schrank(this),(d,s)=>d.copy(s,createInterceptor));
        this.schiebetuerLinksVorne = arrangement.schiebetuerLinksVorne;
        this.schliessdaempfung = arrangement.schliessdaempfung;
        _.assign(this.materials,arrangement.materials);
        return this;
    }

    compile() {
        APP.model.lazyCreateObject3d(this, () => {
            const o = new ArrangementO3(this);
            if (this.object3d) {
                Scenes.remove(this.object3d);
            }
            this.object3d = o;
            APP.model.add(o);
            return o;
        });

        this.raum.compile();

        /**
         * this._compileAussen() muss zweimal ausgeführt werden, damit die Verdeckung korrekt berechnet werden kann
         * Dies ist insbesondere für den Eckschrank notwendig
         */
        this._compileAussen();
        this._compileAussen();
        this.schraenke.forEach(x => x.sizeZisInnenmass = x.verdeckungsIntervale.length>0 && x._getFrontOffset()>0.01);
        if (this.schraenke.some(x=>x.sizeZisInnenmass)) {
            this._compileAussen();
        }
        this.schraenke.forEach(x => x.compileInnen());
        this.compiled = true;
        APP.updatePrices(this.toJSON());
        this.compilePreisLazy();
    }

    compilePreisLazy = _.debounce(()=>{
        this.preis = this.preisberechnung.getPreis();
    },300);

    _compileAussen() {
        this.schraenke.forEach(x => x.compileBox());
        this._compileLocations();
        this._compileVerdeckung();
    }

    _compileLocations() {
        const raumBox = this.raum.box.clone();
        raumBox.expandByVector(v3(-2,-Math.max(0.1, APP.settings.getFloat('schrank_abstand_unten')),-2));
        if (this.schraenke.length==1) {
            const schrank = this.schraenke[0];
            schrank.quaternion.copy(Maths3.quaternion.zero);
            Scenes.bring({
                what: [schrank,schrank.box.grid(0.5,0,0)],
                to: [this.raum,raumBox.grid(0.5,0,0)],
                translate: true
            });
        } else {
            this.schraenke[0].quaternion.copy(Maths3.quaternion.y90);
            this.schraenke[1].quaternion.copy(Maths3.quaternion.zero);
            const leftPrio = this.schraenke[0].size.z < this.schraenke[1].size.z;
            if (leftPrio) {
                Scenes.bring({
                    what: [this.schraenke[1],this.schraenke[1].box.grid(0,0,0)],
                    to: [this.raum,raumBox.grid(0,0,0)],
                    translate: true
                });
                Scenes.bring({
                    what: [this.schraenke[0],this.schraenke[0].box.grid(1,0,0)],
                    to: [this.schraenke[1],this.schraenke[1].box.grid(0,0,1)],
                    translate: true
                });
            } else {
                Scenes.bring({
                    what: [this.schraenke[0],this.schraenke[0].box.grid(1,0,0)],
                    to: [this.raum,raumBox.grid(0,0,0)],
                    translate: true
                });
                Scenes.bring({
                    what: [this.schraenke[1],this.schraenke[1].box.grid(0,0,0)],
                    to: [this.schraenke[0],this.schraenke[0].box.grid(1,0,1)],
                    translate: true
                });
            }
        }
    }

    _compileVerdeckung() {
        const verdeckungMinDistance = 10;

        // Betrachte die Schränke stets von oben, 2d als Rechtecke in der Ebene
        // Eine Verdeckung ergibt sich, wenn der Abstand zweier Seiten (Linien-Segment) zweier Schränke
        // einen Mindestwert unterschreitet
        // gehe alle Kombinationsmöglichkeiten der Schränke durch
        const ecke = (schrank,x,z) => {
            const vec3 = schrank.localToWorld(schrank.box.grid(v3(x,0,z)));
            return v2(vec3.x,vec3.z);
        }

        const kanten = (schrank) => {
            const ecken = [ecke(schrank,0,0),ecke(schrank,0,1),ecke(schrank,1,1),ecke(schrank,1,0)];
            const element = [_.first(schrank.elements),null,_.last(schrank.elements),null];
            const edges = [];
            for (let i=0;i<ecken.length;i++) {
                const j = (i+1)%ecken.length;
                const e1 = ecken[i];
                const e2 = ecken[j];
                const line = new Line2d().setFromPoints(e1,e2);
                line.element = element[i];
                edges.push(line);
            }
            return edges;
        };

        const schrankKombinationen = iterator=>{
            for (let i=0;i<this.schraenke.length;i++) {
                for (let j=i+1;j<this.schraenke.length;j++) {
                    const a = this.schraenke[i];
                    const b = this.schraenke[j];
                    iterator(a,b);
                }
            }
        };

        const lineProjectionInterval = (line,p1,p2) => ClosedInterval.order([
            line.origin.distanceTo(line.projectPoint(p1)),
            line.origin.distanceTo(line.projectPoint(p2))]);

        const verdeckung = (a,oeffnung,b,kante) => {



            const oeffnungInterval = lineProjectionInterval(oeffnung,oeffnung.origin,oeffnung.point2);

            const kanteInterval = lineProjectionInterval(oeffnung,kante.origin,kante.point2);

            const points = oeffnung.pointsWhereDistanceToLineIs(kante,verdeckungMinDistance);

            let verdeckungsInterval = kanteInterval;
            if (points==null) {
                const parallelDistance = oeffnung.distanceToPoint(kante.origin);

                if (parallelDistance>verdeckungMinDistance) {
                    return;
                }
            } else {
                const pointsInterval = lineProjectionInterval(oeffnung,points[0],points[1]);

                verdeckungsInterval = ClosedInterval.intersection(kanteInterval,pointsInterval);

            }
            verdeckungsInterval = verdeckungsInterval ? ClosedInterval.intersection(oeffnungInterval,verdeckungsInterval) : null;

            if (verdeckungsInterval) {
                a.verdeckungsIntervale.push(verdeckungsInterval);
                a.verdeckungsIntervale = ClosedInterval.removePoints(ClosedInterval.maybeUnion(a.verdeckungsIntervale));
                if (kante.element) {
                    const elemente = a.elements.filter(e=>ClosedInterval.intersects([e.box.min.x,e.box.max.x],verdeckungsInterval));
                    kante.element.verdecktAnderenSchrank = { schrank: a, verdeckungsInterval, elemente };
                    elemente.forEach(e=>e.verdecktVonElementen.push(kante.element));
                }
            }

        };

        const setSchrankVerdeckungen = (a,b) => {
            // Bei der Verdecken wird geprüft, ob die Öffnungsseite des Schrankes a von links oder rechts aus von
            // einem anderen Schrank b verdeckt ist.
            const oeffnung = new Line2d().setFromPoints(ecke(a,0,1),ecke(a,1,1));
            kanten(b).forEach(kante=>verdeckung(a,oeffnung,b,kante));
        };

        const setNoVerdeckung = () => {
            this.schraenke.forEach(a=>{
                a.verdeckungsIntervale=[];
                a.elements.forEach(e=>{
                    e.verdecktAnderenSchrank = null;
                    e.unverdeckt.linksVerdeckt = false;
                    e.unverdeckt.rechtsVerdeckt = false;
                    e.unverdeckt.box.copy(e.box);
                    e.unverdeckt.size.copy(e.size);
                    e.verdecktVonElementen = [];
                });
            });
        };

        const setElementVerdeckung = (element) => {
            const elementInterval = [element.box.min.x,element.box.max.x];
            const fuge = APP.settings.getMinValue('fugen_abstand');
            const verdeckungsInterval = element.schrank.verdeckungsIntervale
                .map(i=>ClosedInterval.intersection(elementInterval,i))
                .filter(i=>i!==null)
                .map(i=>ClosedInterval.intersection(elementInterval,ClosedInterval.expandBy(i,fuge)))
                .reduce((acc,i)=>ClosedInterval.union(acc,i),null);
            if (verdeckungsInterval && ClosedInterval.size(verdeckungsInterval)>0.01) {
                const l = verdeckungsInterval[0]-elementInterval[0];
                const r = elementInterval[1]-verdeckungsInterval[1];
                const unverdecktesInterval = l<r ? [verdeckungsInterval[1],elementInterval[1]] : [elementInterval[0],verdeckungsInterval[0]];
                const unverdeckt = element.unverdeckt;
                unverdeckt.box.min.x = unverdecktesInterval[0];
                unverdeckt.box.max.x = unverdecktesInterval[1];
                unverdeckt.size.x = unverdeckt.box.max.x - unverdeckt.box.min.x;
                unverdeckt.linksVerdeckt = Math.abs(element.box.min.x-unverdeckt.box.min.x)>0.01;
                unverdeckt.rechtsVerdeckt = Math.abs(element.box.max.x-unverdeckt.box.max.x)>0.01;
            }
        };

        setNoVerdeckung();
        schrankKombinationen((a,b)=>{
            setSchrankVerdeckungen(a,b);
            setSchrankVerdeckungen(b,a);
        });
        this.schraenke.forEach(a=>a.elements.forEach(e=>setElementVerdeckung(e)));

    }

    getHinweistexteFormatted() {
        if (Commons.isEmpty(this.hinweistexteMap)) {
            return null;
        }
        return {
            title: Commons.isEmpty(this.hinweistexteMap) ? '' : I18N(this.hinweistexteMap.title),
            messages: _.keys(this.hinweistexteMap).filter(key => key !== 'title').map(key => I18N(...([key].concat(this.hinweistexteMap[key])))),
        };
    }

    setHinweisText(i18nKey, ...params) {
        //debugger;
        this.hinweistexteMap[i18nKey] = params;
    }

    setHinweisTextAndTitle(title, i18nKey, ...params) {
        this.hinweistexteMap.title = title;
        const userParams = params.map(p => isNaN(p) ? p : Helper.round(p))
        this.setHinweisText(i18nKey, ...userParams);
    }

    resetHinweistexte() {
        this.hinweistexteMap = {};
    }

    clearLocked(direction = null) {
        this.schraenke.forEach(c => c.clearLocked(direction));
    }

    getElements() {
        return this.schraenke.reduce((acc,schrank)=>acc.concat(schrank.elements),[]);
    }

    getInterieurs() {
        return _.flattenDeep(this.schraenke.map(schrank=>schrank.elements.map(element=>element.interieurs)));
    }

    toggleSchrank() {
        this.currentSchrankIndex = (this.currentSchrankIndex+1) % this.schraenke.length;
        APP.updateUI();
    }

    getBox3World() {
        const unionBox = new THREE.Box3();
        this.schraenke.forEach(s => s.getBox3World(unionBox));
        return unionBox;
    }

    iterate(iterator) {
        iterator(this);
        this.schraenke.forEach(x=>x.iterate(iterator));
        iterator(this.kunde);
        iterator(this.raum);
    }

    iterateElements(iterator) {
        this.schraenke.forEach(s=>s.iterateElements(iterator));
    }

    iterateInterieur(iterator) {
        this.iterateElements(e=>e.interieurs.forEach(x=>x.iterate(iterator)));
    }

    /**
     * ist false, falls dieses Objekt Teil des APP.arrangement ist.
     */
    isOrphaned() {
        return this!==APP.arrangement;
    }

    getChangedProductsHinweistexte() {
        // TODO, Rückgabe von Texten, falls Produkte nicht mehr verfügbar sind
        // das hier war aus dem DataProvider und machte auf bescheurte Weise die Ermittlung, welche Produkte nicht mehr
        // vorhanden sind
        // const schrankA = arrangement.schraenke[0];
        // let schrankC = configuration.schraenke[0];
        // let griff = _.get(schrankC, 'doorHandle', null);
        // if (griff !== null && griff !== schrankA['doorHandleArtikelnummer']) {
        //     result.push('missingGriffeText')
        // }
        // if (schrankA.materials.corpus !== schrankC.materials.corpus || schrankA.materials.front !== schrankC.materials.front) {
        //     result.push('missingTextureFrontText')
        // }
        return [];
    }

    isSchwebetuer() {
        return this.Modell.Name === 'Schwebetuerenschrank';
    }

    isShojituer() {
        return this.isShojituerInnen() || this.isShojituerAussen();
    }

    isShojituerInnen() {
        return this.Modell.Name === 'Shojituerenschrank';
    }

    isShojituerAussen() {
        return this.Modell.Name === 'ShojituerenschrankAussenliegend';
    }

    isSchwebetuerOrShoji() {
        return this.isSchwebetuer() || this.isShojituer();
    }

    isSchiebetuerVorneAuswahl() {
        return (this.isSchwebetuer() && this.selectedSchrank?.schwebetuer?.schwebetuerFluegel?.length==2) ||
            (this.isShojituer() && this.selectedSchrank?.shojituer?.fluegel?.length==2);
    }

    hasSchliessDaempfungsOption() {
        return this.isSchwebetuer() || this.isShojituerAussen();
    }

    isEckschrank() {
        return this.Modell.Name=='Eckschrank';
    }

}
